import type {
    AutomationRecommendationStateTransport,
    JobProfileTaskTransport,
    ProfileTaskAssessmentTransport,
} from '~/shared/data-contract/profile-task';

import {
    type AutomationAssessment,
    type AutomationAssessmentState,
    CAPABILITY_FACTOR,
    ERROR_COST_RATING,
} from '../automation-assessment/automation-assessment-model';

export const workflowStepIdAt = (index: number): string => `step-${index}`;

/** Resolves the backend's labels to the display factors and keys steps like the task model does. */
export const mapAutomationAssessment = (transport: ProfileTaskAssessmentTransport): AutomationAssessment => ({
    businessImpact:
        transport.businessImpactDrivers === undefined
            ? undefined
            : {
                  drivers: transport.businessImpactDrivers,
                  rationale: transport.businessImpactRationale,
                  value: transport.businessImpact,
              },
    methodVersion: transport.methodVersion,
    steps: (transport.steps ?? []).map((step) => ({
        capability: CAPABILITY_FACTOR[step.capability],
        capabilityLevel: step.capability,
        coordinationShare: step.coordinationShare,
        coreInteractionShare: step.coreInteractionShare,
        digitalShare: step.digitalShare,
        effortShare: step.effortShare,
        errorCost: ERROR_COST_RATING[step.errorCost],
        errorCostLevel: step.errorCost,
        mandatedHumanInvolvement: step.mandatedHumanInvolvement,
        normalizedEffortShare: step.normalizedEffortShare,
        rationale: step.rationale,
        score: step.score,
        workflowStepId: workflowStepIdAt(step.stepIndex),
    })),
    summary: transport.summary,
    taskScore: transport.taskScore,
    taskScoreLower: transport.taskScoreLower,
    taskScoreUpper: transport.taskScoreUpper,
});

export type ProfileTaskAutomation = {
    readonly assessment: AutomationAssessment | undefined;
    /** Undefined when the backend never scheduled one; queued or started means it is on its way. */
    readonly state: AutomationAssessmentState | undefined;
    /** Undefined when no automation recommendation was ever requested for the task. */
    readonly recommendationState: AutomationRecommendationStateTransport | undefined;
};

/** Profile tasks and work tasks carry the same assessment fields, so both tiles read them through this. */
export const mapProfileTaskAutomation = (
    task: Pick<JobProfileTaskTransport, 'assessment' | 'assessmentState' | 'automationRecommendationState'>,
): ProfileTaskAutomation => ({
    assessment: task.assessment === undefined ? undefined : mapAutomationAssessment(task.assessment),
    recommendationState: task.automationRecommendationState,
    state: task.assessmentState,
});

/** True while the backend still owes a result: scheduled but not finished, and nothing stored yet. */
export const isAutomationAssessmentPending = (automation: ProfileTaskAutomation | undefined): boolean =>
    automation !== undefined &&
    automation.assessment === undefined &&
    (automation.state === 'queued' || automation.state === 'started');
